/**
 * Claude/Anthropic flat-rate billing calibration (claude_anthropic_flat_rate_billing_
 * calibration_2026_08_12 — operator ask directly following the DeepSeek native-usage-
 * capture proxy work: "together with our deepseek stuff we'd have good billing across
 * Accounts and Task Token Usage and DeepSeek Wallet Reconciliation and our new widget
 * Claude Wallet Reconciliation").
 *
 * Unlike DeepSeekWalletPanel, this needs NO operator-entered top-up form: both halves
 * of the ratio already exist as real captured data — named for what they literally are
 * (operator correction 2026-08-13: an earlier implied/actual naming read backwards from
 * how anyone actually thinks about a subscription bill) —
 *   subscription $       = the REAL dollar figure: Anthropic's own %-of-weekly-limit
 *                           signal (UsagePoller, live since 2026-05-29, reading the real
 *                           `anthropic-ratelimit-unified-*` response headers) x the
 *                           account's prorated weekly subscription budget. What you
 *                           actually pay.
 *   metered-equivalent $ = a HYPOTHETICAL figure: captured token usage priced at
 *                           Anthropic's published METERED list rates (model_pricing.py),
 *                           already sitting in task_usage.spend_usd. What the bill would
 *                           be if billed per-token instead of flat.
 * This panel is a pure read-only view over server/state_store/account_usage.py's
 * compute_claude_wallet_reconciliation() — see that module for the full computation
 * contract and why neither half needed new capture infrastructure.
 *
 * Self-contained fetch + poll, same shape as DeepSeekWalletPanel.tsx: pure exported
 * formatters here (vitest-covered), the React shell only owns fetch/poll state.
 */

import { lazy, Suspense, useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import { LIFETIME_WINDOW_HOURS } from "./DeepSeekWalletPanel";
import type { ClaudeAccountWalletReconciliationView, ClaudeAccountWalletWindowReconciliationView } from "./types";

// Lazy: recharts (pulled in by UsageTimeSeriesModal) only loads once the operator
// actually opens the usage-over-time modal, not on every Dashboard page load — see
// ao_control_plane_read_models_and_request_path_2026_09_04.md [UI] P0.
const UsageTimeSeriesModal = lazy(() =>
  import("./UsageTimeSeriesModal").then((m) => ({ default: m.UsageTimeSeriesModal })),
);

const POLL_INTERVAL_MS = 60000;

// 1h/5h/24h/7d/30d/90d (2026-08-18, operator ask to match TaskUsageWindows.tsx's window
// set across every wallet panel — 30d added cost_ranked_model_routing_rebuild_2026_08_28 [UI] P1). The widest window is labelled "90d" (a 90-day cap, not an unbounded "Lifetime"). It is the same windowed endpoint at the
// server's max window_hours, not a separate concept — see LIFETIME_WINDOW_HOURS.
const WINDOW_OPTIONS: { hours: number; label: string }[] = [
  { hours: 1, label: "1h" },
  { hours: 5, label: "5h" },
  { hours: 24, label: "24h" },
  { hours: 168, label: "7d" },
  { hours: 720, label: "30d" },
  { hours: LIFETIME_WINDOW_HOURS, label: "90d" },
];

// ── pure display mappers (vitest-covered) ──────────────────────────────────

/** "$1.2345" — "—" for null (nothing captured/computed yet). */
export function fmtClaudeUsdOrDash(amount: number | null): string {
  if (amount === null) return "—";
  return `$${amount.toFixed(4)}`;
}

/** "12%" — "—" for null (no /usage probe yet). */
export function fmtWeeklyPctOrDash(pct: number | null): string {
  if (pct === null) return "—";
  return `${pct}%`;
}

/** "2.26x" — "—" whenever either side of the ratio is unknown. Never rounds to a
 * misleadingly-precise figure from a partial-window or unpriced sample. */
export function fmtBoostMultiplierOrDash(multiplier: number | null): string {
  if (multiplier === null) return "—";
  return `${multiplier.toFixed(2)}x`;
}

/** Why a row has no multiplier yet, or a caveat about the window it reflects — surfaced
 * as a title/tooltip rather than silently leaving a bare dash. */
export function claudeRowCaveat(row: ClaudeAccountWalletReconciliationView): string | null {
  if (row.monthly_price_usd === null) return "No published monthly price for this tier — subscription $ unavailable";
  if (row.weekly_pct === null) return "No /usage probe yet for this account";
  if (row.unpriced_turns > 0) {
    return (
      `${row.unpriced_turns} turn(s) this window used a model with no registered rate card — ` +
      "metered-equivalent $ withheld"
    );
  }
  if (row.metered_equivalent_spend_usd === null) return "No captured spend yet this window";
  if (!row.window_is_complete) return "Window still open (not yet reset) — a partial sample, not a full week";
  return null;
}

/** Same shape as claudeRowCaveat above, for the windowed view — plus the two caveats
 * specific to a bounded window: reset_crossed and tier_changed_mid_window. Both the
 * reset-aware delta (_claude_subscription_spend_across_resets_and_tiers) and a
 * mid-window tier change (Pro -> Max20 etc.) are priced correctly per-segment at the
 * tier/rate in force at that segment's own starting sample — these are surfaced as
 * informational notes about what happened in the window, not warnings that the number
 * is wrong. */
export function claudeWindowRowCaveat(row: ClaudeAccountWalletWindowReconciliationView): string | null {
  if (row.monthly_price_usd === null) return "No published monthly price for this tier — subscription $ unavailable";
  if (row.start_sample_at === null || row.end_sample_at === null) {
    return "No /usage samples reach back this far yet for this account";
  }
  if (row.unpriced_turns > 0) {
    return (
      `${row.unpriced_turns} turn(s) this window used a model with no registered rate card — ` +
      "metered-equivalent $ withheld"
    );
  }
  if (row.metered_equivalent_spend_usd === null) return "No captured spend yet this window";
  const notes: string[] = [];
  if (row.reset_crossed) {
    notes.push("Window crosses a weekly reset — the delta is chained correctly across the reset.");
  }
  if (row.tier_changed_mid_window) {
    notes.push("This account's tier changed within the window — each segment was priced at the tier in force then.");
  }
  return notes.length > 0 ? notes.join(" ") : null;
}

// ── component ────────────────────────────────────────────────────────────

export function ClaudeWalletPanel({ baseUrl }: { baseUrl: string }) {
  const [usageTsOpen, setUsageTsOpen] = useState(false);
  const [rows, setRows] = useState<ClaudeAccountWalletReconciliationView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Windowed view (2026-08-18, operator ask to match TaskUsageWindows.tsx's window set)
  // — a SEPARATE fetch/poll from the current-week `rows` above: different endpoint,
  // re-fetched whenever the toggle selection changes, same pattern as DeepSeek's
  // windowed sections.
  const [windowHours, setWindowHours] = useState(WINDOW_OPTIONS[0].hours);
  const [windowRows, setWindowRows] = useState<ClaudeAccountWalletWindowReconciliationView[] | null>(null);
  const [windowError, setWindowError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getClaudeWalletReconciliation(baseUrl);
        if (!cancelled) {
          setRows(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadWindow = async () => {
      try {
        const result = await api.getClaudeWalletWindowReconciliation(baseUrl, windowHours);
        if (!cancelled) {
          setWindowRows(result);
          setWindowError(null);
        }
      } catch (e) {
        if (!cancelled) setWindowError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadWindow();
    const id = setInterval(() => void loadWindow(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, windowHours]);

  return (
    <Panel
      title="Claude Wallet Reconciliation"
      collapsible
      tone="accounts"
      right={
        <button className="btn btn-xs" onClick={() => setUsageTsOpen(true)}>
          Usage over time
        </button>
      }
    >
      {usageTsOpen && (
        <Suspense fallback={<div className="task-usage-pending">loading…</div>}>
          <UsageTimeSeriesModal
            baseUrl={baseUrl}
            open={usageTsOpen}
            onClose={() => setUsageTsOpen(false)}
            initialProvider="anthropic"
          />
        </Suspense>
      )}
      <div className="usage-panel-hint">
        Per-account: Anthropic's own %-of-weekly-limit signal converted to a subscription $ figure (what you actually
        pay), vs. captured token usage priced at metered list rates (what it would cost per-token) — the ratio is how
        much more compute value the flat-rate plan is extracting vs. metered API pricing.
      </div>
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !rows && <div className="task-usage-pending">loading…</div>}
      {!error && rows && rows.length === 0 && (
        <div className="usage-panel-hint">No Anthropic-subscription accounts declared.</div>
      )}
      {!error && rows && rows.length > 0 && (
        // Scoped container so e2e specs can target this table's rows specifically — the
        // windowed table below reuses the same "Account"/"Tier"/"Boost" shape and, for
        // the same account, can render a DIFFERENT boost value, so an unscoped selector
        // would be ambiguous (same pattern as DeepSeekWalletPanel's
        // .deepseek-wallet-lifetime/.deepseek-wallet-window split).
        <div className="claude-wallet-lifetime">
          <table className="acc-usage-table acc-usage-table-breakdown">
            <thead>
              <tr>
                <th>Account</th>
                <th>Tier</th>
                <th>Weekly %</th>
                <th>Subscription $</th>
                <th>Metered-equivalent $</th>
                <th>Boost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.account_id}>
                  <td title={r.primary_email ?? undefined}>{r.label}</td>
                  <td className="mono">{r.tier}</td>
                  <td className="mono">{fmtWeeklyPctOrDash(r.weekly_pct)}</td>
                  <td className="mono">{fmtClaudeUsdOrDash(r.subscription_spend_usd)}</td>
                  <td className="mono">{fmtClaudeUsdOrDash(r.metered_equivalent_spend_usd)}</td>
                  <td className="mono acc-usage-total" title={claudeRowCaveat(r) ?? undefined}>
                    {fmtBoostMultiplierOrDash(r.boost_multiplier)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="usage-panel-hint">
        Windowed view: the same ratio bounded by an explicit window instead of each account's own weekly reset, so
        different accounts (different reset days) are comparable over the SAME wall-clock span.
      </div>
      <div className="task-usage-provider-toggle">
        {WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            className={`btn btn-xs ${windowHours === opt.hours ? "active" : ""}`}
            onClick={() => setWindowHours(opt.hours)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {windowError && <div className="task-usage-error">Failed to load window: {windowError}</div>}
      {!windowError && !windowRows && <div className="task-usage-pending">loading…</div>}
      {!windowError && windowRows && windowRows.length === 0 && (
        <div className="usage-panel-hint">No Anthropic-subscription accounts declared.</div>
      )}
      {!windowError && windowRows && windowRows.length > 0 && (
        <div className="claude-wallet-window">
          <table className="acc-usage-table acc-usage-table-breakdown">
            <thead>
              <tr>
                <th>Account</th>
                <th>Tier</th>
                <th>Subscription $</th>
                <th>Metered-equivalent $</th>
                <th>Boost</th>
              </tr>
            </thead>
            <tbody>
              {windowRows.map((r) => (
                <tr key={r.account_id}>
                  <td title={r.primary_email ?? undefined}>{r.label}</td>
                  <td className="mono">{r.tier}</td>
                  <td className="mono">{fmtClaudeUsdOrDash(r.subscription_spend_usd)}</td>
                  <td className="mono">{fmtClaudeUsdOrDash(r.metered_equivalent_spend_usd)}</td>
                  <td
                    className={`mono acc-usage-total${
                      r.reset_crossed || r.tier_changed_mid_window ? " acc-usage-caveat" : ""
                    }`}
                    title={claudeWindowRowCaveat(r) ?? undefined}
                  >
                    {fmtBoostMultiplierOrDash(r.boost_multiplier)}
                    {r.reset_crossed || r.tier_changed_mid_window ? " ⚠" : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
